import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const uagcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const uagcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}];
interface UagcProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const uagcPrograms: UagcProgram[] = [
// Associate - Business
{
  shortTitle: 'A.A. - Business',
  longTitle: 'Associate of Arts (A.A.) in Business',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54516'
}, {
  shortTitle: 'A.A. - Organizational Management',
  longTitle: 'Associate of Arts (A.A.) in Organizational Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54517'
},
// Associate - Education
{
  shortTitle: 'A.A. - Early Childhood Education',
  longTitle: 'Associate of Arts (A.A.) in Early Childhood Education',
  degreeLevel: 'associate',
  areaOfStudy: 'education',
  mProgram: '1639'
},
// Associate - Liberal Arts
{
  shortTitle: 'A.A. - Military Studies',
  longTitle: 'Associate of Arts (A.A.) in Military Studies',
  degreeLevel: 'associate',
  areaOfStudy: 'liberal-arts',
  mProgram: '54693'
},
// Bachelor's - Business
{
  shortTitle: 'B.A. - Accounting',
  longTitle: 'Bachelor of Arts (B.A.) in Accounting',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1610'
}, {
  shortTitle: 'B.A. - Business Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1608'
}, {
  shortTitle: 'B.A. - Business Leadership',
  longTitle: 'Bachelor of Arts (B.A.) in Business Leadership',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1611'
}, {
  shortTitle: 'B.A. - Finance',
  longTitle: 'Bachelor of Arts (B.A.) in Finance',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1614'
}, {
  shortTitle: 'B.A. - Human Resources Management',
  longTitle: 'Bachelor of Arts (B.A.) in Human Resources Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1618'
}, {
  shortTitle: 'B.A. - Marketing',
  longTitle: 'Bachelor of Arts (B.A.) in Marketing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1619'
}, {
  shortTitle: 'B.A. - Organizational Management',
  longTitle: 'Bachelor of Arts (B.A.) in Organizational Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1621'
}, {
  shortTitle: 'B.A. - Project Management',
  longTitle: 'Bachelor of Arts (B.A.) in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1622'
},
// Bachelor's - Criminal Justice
{
  shortTitle: 'B.A. - Social and Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Social and Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '1642'
},
// Bachelor's - Education
{
  shortTitle: 'B.A. - Early Childhood Development w/ Differentiated Instruction',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Development w/ Differentiated Instruction',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1645'
}, {
  shortTitle: 'B.A. - Early Childhood Education',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1612'
}, {
  shortTitle: 'B.A. - Early Childhood Education Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1613'
}, {
  shortTitle: 'B.A. - Education Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Education Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1637'
}, {
  shortTitle: 'B.A. - Instructional Design',
  longTitle: 'Bachelor of Arts (B.A.) in Instructional Design',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1641'
},
// Bachelor's - Information Technology
{
  shortTitle: 'B.A. - Business Information Systems',
  longTitle: 'Bachelor of Arts (B.A.) in Business Information Systems',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1609'
}, {
  shortTitle: 'B.A. - Information Technology',
  longTitle: 'Bachelor of Arts (B.A.) in Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1625'
}, {
  shortTitle: 'B.S. - Computer Software Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Software Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1628'
}, {
  shortTitle: 'B.S. - Cyber & Data Security Technology',
  longTitle: 'Bachelor of Science (B.S.) in Cyber & Data Security Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1640'
},
// Bachelor's - Social & Behavioral Science
{
  shortTitle: 'B.A. - Applied Behavioral Science',
  longTitle: 'Bachelor of Arts (B.A.) in Applied Behavioral Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1643'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1671'
}, {
  shortTitle: 'B.A. - Sociology',
  longTitle: 'Bachelor of Arts (B.A.) in Sociology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1672'
}];
export function UAGCLandingPage() {
  usePageMeta('University of Arizona Global Campus | Online Degree Programs', 'Explore flexible online degree programs at The University of Arizona Global Campus. Accredited programs in Business, Criminal Justice, and Education designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    'social-behavioral-science': 'Social & Behavioral Science'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  // Get available areas based on selected degree level
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    uagcPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  // Get available programs based on selected area and degree level
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return uagcPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = uagcPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'The University of Arizona Global Campus',
            logo: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
            primaryColor: '#1C2B39',
            accentColor: '#CC0033',
            backUrl: '/colleges/university-of-arizona-global-campus',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2622" data-mp="25lfaexkmjrm">
          <div data-id="element-2623" data-mp="25v4i0askmw9">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2624" data-mp="227evc8gwkmt">
              Degree Level
            </label>
            <div className="relative" data-id="element-2625" data-mp="3kaexwm1pb6r3">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800" data-id="element-2626" data-mp="3kbgipi8ir7m0">
                <option value="" data-id="element-2627" data-mp="74erm414g4q7">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-2628" data-mp="14jsovvosxv7f">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2629" data-mp="2tzmmao6zvy12">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2630" data-mp="2y0on39gk8i55" />
              </div>
            </div>
          </div>

          <div data-id="element-2631" data-mp="2k9a0ksng1ytc">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2632" data-mp="2k9tkgewnmxa0">
              Area of Study
            </label>
            <div className="relative" data-id="element-2633" data-mp="2ae9u080v0qsn">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2634" data-mp="2fbrv5c8my1q3">
                <option value="" data-id="element-2635" data-mp="emcejsltakqw">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-2636" data-mp="nhk1qzh1eqtq">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2637" data-mp="xcu1thnvko4v">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2638" data-mp="sdqyc1njmpz0" />
              </div>
            </div>
          </div>

          <div data-id="element-2639" data-mp="16drzli7pzzf0">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2640" data-mp="16c7eskb7juuc">
              Program
            </label>
            <div className="relative" data-id="element-2641" data-mp="387hsp261hdwk">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2642" data-mp="344uxcra5taum">
                <option value="" data-id="element-2643" data-mp="39qoxzx0ye985">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2644" data-mp="3ipipj8zfg9eu">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2645" data-mp="xzl0xcxwdyf6">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2646" data-mp="12y4nnfkbfe8l" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-2647" data-mp="1d2urjqza4sfg">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2648" data-mp="13c078s5lg2wn">
          <div data-id="element-2649" data-mp="zchjlome1dq4">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2650" data-mp="zd13havlmc6s">
              Selected Program
            </label>
            <div className="relative" data-id="element-2651" data-mp="pd0jrntthu22">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2652" data-mp="pg4pwqquezvd">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2653" data-mp="hdpjc9gx60q8">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2654" data-mp="8kw3a9tnexbb">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2655" data-mp="3m1dgnx9k8hp" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2656" data-mp="3uslynr0n0m6t" />

          <div data-id="element-2657" data-mp="3upzpxo20g0n0">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2658" data-mp="3pszl1ggwd3xh">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2659" data-mp="3gt327vh6uzac">
              <option value="" data-id="element-2660" data-mp="1ml8y8rof75b5">-- Please Select --</option>
              {uagcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2661" data-mp="1ri95h5x0ae3u">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2662" data-mp="21824l7fyp0r7">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2663" data-mp="13ny0xcmcmlyc">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2664" data-mp="18kg0ly2tnu3t">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2665" data-mp="1hmfjkvxed89e">
              <option value="" data-id="element-2666" data-mp="2t2mcxcr121vu">-- Please Select --</option>
              {uagcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2667" data-mp="txmbzektnpr1">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2668" data-mp="12uddi6i57wt0">{errors.startDate}</p>}
          </div>

          <div data-id="element-2669" data-mp="xvjzmcyy23sb">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2670" data-mp="a7seqkzc8iff">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2671" data-mp="jxver54vmwn9">
              <option value="" data-id="element-2672" data-mp="3nl40og8h9qzq">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2673" data-mp="3inc3b6p2dco5">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2674" data-mp="22xbfxard7gwn">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2675" data-mp="1p5y1ucc87b90">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2676" data-mp="1p321yigtichx">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2677" data-mp="2lt1hwxzasdaj" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2678" data-mp="27xgz02jkjv34">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2679" data-mp="1byh7pf9nusf8">
          <div data-id="element-2680" data-mp="1bx61z9wnnalh">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2681" data-mp="1bwo283asae8t">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2682" data-mp="12zdo8a6l0b07" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2683" data-mp="a953caknvchx">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2684" data-mp="5q0hr7rcryh">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2685" data-mp="53ruv8amwdp9" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2686" data-mp="etkwphkz5egb">
                <div data-id="element-2687" data-mp="ev5ox0jtglb0">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2688" data-mp="ivg5f758iu44">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="City" data-id="element-2689" data-mp="stcdtr6sfayj" />
                </div>
                <div data-id="element-2690" data-mp="2rlgm70hwugxc">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2691" data-mp="2mkau94w2qxov">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="State" data-id="element-2692" data-mp="2wi7agb3vcitu" />
                </div>
              </div>
              <div data-id="element-2693" data-mp="3t8gda5c3kanx">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2694" data-mp="3t7y8ulfa6q4x">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`} placeholder="123 Main St" data-id="element-2695" data-mp="bqk6t8ptb846" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2696" data-mp="3g5tsiqn1ls29">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2697" data-mp="3662wulmqx70l">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2698" data-mp="36749gujeu1kf">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2699" data-mp="3lnudkh0qgbli" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2700" data-mp="3hlppdxt20769">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2701" data-mp="2xz1fof0jvto9">
          <div className="grid grid-cols-2 gap-3" data-id="element-2702" data-mp="2twwqptl1rl7y">
            <div data-id="element-2703" data-mp="2ty7rdkj74cuu">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2704" data-mp="2tyrah4kl17ag">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2705" data-mp="2k135zmx6clf3" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2706" data-mp="1dffvuvgwpbzo">{errors.firstName}</p>}
            </div>
            <div data-id="element-2707" data-mp="gri0lqgwf6ru">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2708" data-mp="gs1dgsroz4z0">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2709" data-mp="pp1zwtv7aszt" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2710" data-mp="38vbtzrlzarfv">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2711" data-mp="3cv2skhhqybuy">
            <div data-id="element-2712" data-mp="2ywma5taiuwwz">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2713" data-mp="2yv1gs6zy1k0p">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2714" data-mp="2p4qc2i8fr6sr" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2715" data-mp="267fb8t75o5b7">{errors.email}</p>}
            </div>
            <div data-id="element-2716" data-mp="2b68r2s9x08ej">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2717" data-mp="2b6s0tlpevqhh">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2718" data-mp="1nkga1qw59nvr" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2719" data-mp="26hqxme8eysvd">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#CC0033] focus:ring-[#CC0033] cursor-pointer flex-shrink-0" data-id="element-2720" data-mp="2kb8flulgbxja">
            <>
                By selecting Request Information, I consent to receive calls,
                emails, and texts from the University of Arizona Global Campus
                (UAGC) which may be sent using automated or prerecorded
                technology. Message and data rates may apply; frequency may
                vary. This consent is not a condition of applying, enrolling, or
                purchase. I may contact UAGC directly at 866-711-1700, I may opt
                out of SMS anytime by replying STOP, and can text HELP for
                assistance.{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/uwnAfhD-utqnhD___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6NWFlZDplNzRhOWMyMmMxZmRmOTJlOGI0ZmI1NTU3NjBmMWQxMDRlN2M5NzVmNmRhN2M2NDY5YWFlNmZhNWZjYzc1OGZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2721" data-mp="3ozujvj0f22v5">
                  Privacy Policy
                </a>
                ,{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/yjwrx-fsi-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6ODA3ZTowMTFlY2JiNTVhZjhmMjE3ODhjMGMwYThmZDczMmVhMTk2MGI3ZmUzZGNmYWQ2NGM1M2FlMDY3ZTBmNjAxM2RiOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2722" data-mp="1n9vo699mj7l7">
                  Terms and Conditions
                </a>{' '}
                of Usage, and{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/xrx-yjwrx-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6OWIyNDpmZmQyOTUwMDZmNzIwZTRkMjdkY2NmOWU1NjZhMWM3OGM3N2QyNGE2YmExYmViZmZkYWU1MjQ5NTJjNjA5MjZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-2723" data-mp="1w2p6ygoa4p3t">
                  SMS Terms and Conditions
                </a>{' '}
                apply.
            </>
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-2724" data-mp="24k7n0z4zggnd">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2725" data-mp="2s90ofb4djgpd">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2726" data-mp="2id71cxt144tc" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#CC0033] to-[#A50028] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2727" data-mp="2nbqtjfy7ucu5">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2728" data-mp="2wsmox2zxhot" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2729" data-mp="37cjyetwapjmd">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2730" data-mp="32e8o6i23973m">
        <img src="https://cdn.magicpatterns.com/uploads/hZrXUkDbsWP7copst5KXLY/UAGC.png" alt="University of Arizona Global Campus Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-2731" data-mp="3g55chwozf24v" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2A3F52] to-[#1A4D80] relative overflow-hidden" data-id="element-2732" data-mp="2jmzg8d7yuqe5">
        {/* Decorative image overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2733" data-mp="mlaaf3v193wr" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-2734" data-mp="dkvmrxuljcq3">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-2735" data-mp="1p0ahsw38vuik">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-2736" data-mp="1tyc2tmk8e3wo">
              University of Arizona Global Campus
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-2737" data-mp="3ejihkwu3q6jn">
              {currentProgram ? currentProgram.longTitle : <>
                  Flexible Online
                  <br data-id="element-2738" data-mp="2zrqd5fvuicwp" />
                  Degree Programs
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-2739" data-mp="2qs3cwxeurd58">
            <form onSubmit={e => e.preventDefault()} id="uagc-lead-form" data-id="element-2740" data-mp="2gvi7wc2u221e">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-2741" data-mp="f2au572vbshm" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2742" data-mp="3k349ylbx5joi" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2743" data-mp="3abhuudwgn0gh">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2744" data-mp="31bl9om9a4jq6">
                <div className="bg-gradient-to-r from-[#CC0033] to-[#A50028] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2745" data-mp="36bh6a0fnd8vp"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2746" data-mp="pmk3eof7a62z">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2747" data-mp="pmtqa34y7x4g">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-2748" data-mp="37r1zqfb2ewsn">
            The University of Arizona Global Campus offers accredited online
            degree programs designed for working adults. With flexible
            scheduling, dedicated support, and a connection to the University of
            Arizona, we help you achieve your educational and career goals.
          </p>
        </div>
      </section>

      {/* Reasons to Choose UAGC */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-2749" data-mp="2jdhyj2l1zlu0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2750" data-mp="2jet2p3ieuvln">
          <div className="mb-10" data-id="element-2751" data-mp="2eddh9acbmn2q">
            <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2752" data-mp="2ed3qhkjgcbvz"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39]" data-id="element-2753" data-mp="2sdel8ynvuamf">
              Reasons to Choose UAGC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2754" data-mp="2oai4ldhwkol4">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2755" data-mp="322oam6rcdf6u">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2756" data-mp="2x4msbo1qpjwf">
                <ClipboardCheckIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2757" data-mp="3c38e7g2c2vs8" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2758" data-mp="373vl4dkvcsh7">
                No SAT, GMAT, GRE, or ACT Required
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2759" data-mp="3qpnp9yr6v9v1">
                Standardized test scores are not required for enrollment.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2760" data-mp="3gpwr9n3f6cq7">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2761" data-mp="3loq5jhqj67rf">
                <ClockIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2762" data-mp="36ztxhkoh4uqt" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2763" data-mp="3brv7t1230be7">
                5- to 6-week Classes*
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2764" data-mp="2y1rs6sxpjfxq">
                Take one class at a time as a full-time student.
              </p>
            </div>
            <div data-id="element-2765" data-mp="2o1iutbrzbogr">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2766" data-mp="2o32bv4lgm3jl">
                <DollarSignIcon className="w-7 h-7 text-[#CC0033]" data-id="element-2767" data-mp="2szsqdnhm8r2w" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-2768" data-mp="29xk1u8sx70ko">
                $0 Application Fee
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2769" data-mp="254h7vzk8dp3y">
                Apply to UAGC risk-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose UAGC */}
      <section className="py-16 bg-gray-50" data-id="element-2770" data-mp="1v5rog2corwll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2771" data-mp="2svmoigxzwlfb">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-2772" data-mp="2nwa03rrh76am">
            <div className="flex-1" data-id="element-2773" data-mp="2nx1ooimno3re">
              <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-2774" data-mp="2iwx6mo63n0z2"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-8" data-id="element-2775" data-mp="2iz9vlla9yblm">
                Why Choose <span className="text-[#CC0033]" data-id="element-2776" data-mp="2dxu69h12dixf">UAGC</span>
              </h2>
              <ul className="space-y-4" data-id="element-2777" data-mp="17itapcfyb2rd">
                <li className="flex items-start" data-id="element-2778" data-mp="12ix6bc3cd9ba">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2779" data-mp="12i5mszn0r21d" />
                  <span className="text-gray-700" data-id="element-2780" data-mp="3e7lj4ce7hl0f">
                    <strong data-id="element-2781" data-mp="3j5dbt8modb5o">Transfer approved college credits</strong> towards
                    your bachelor's program*
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2782" data-mp="3o35cos5as7m0">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2783" data-mp="3svos7unjpctg" />
                  <span className="text-gray-700" data-id="element-2784" data-mp="1pezzi0fcrqt">
                    <strong data-id="element-2785" data-mp="1olwqr0ockdf">
                      College credit for relevant work/life experience
                    </strong>{' '}
                    through Prior Learning Assessment & Experiential Learning
                    Credit Programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2786" data-mp="fnvgg0o39wm5">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2787" data-mp="2chgfsswhojvj" />
                  <span className="text-gray-700" data-id="element-2788" data-mp="2hfhxb9e9ob4w">
                    <strong data-id="element-2789" data-mp="2hg9ugoniigxa">Attend for 3 weeks risk-free</strong> to see if UAGC
                    is right for you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2790" data-mp="6tfurpqo2gvr">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2791" data-mp="6v0ml7lljlpx" />
                  <div className="text-gray-700" data-id="element-2792" data-mp="kq312j4iejec">
                    <strong data-id="element-2793" data-mp="grvj5kyb7c1k">
                      One course at a time in an accelerated format
                    </strong>
                    <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-600" data-id="element-2794" data-mp="3p75kgqudkd8a">
                      <li data-id="element-2795" data-mp="3k8u7w8cp3omd">• 5 weeks for undergraduate degrees</li>
                      <li data-id="element-2796" data-mp="1tbbxt2ada2d">• 6 weeks for graduate programs</li>
                      <li data-id="element-2797" data-mp="1vo0wq6gokox">• 9 weeks for doctoral dissertation courses</li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2798" data-mp="35i4t6w00zrdr">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2799" data-mp="35gts53xysxqc" />
                  <span className="text-gray-700" data-id="element-2800" data-mp="31gt2en5f103z">
                    <strong data-id="element-2801" data-mp="2rckzflo26obd">Access the online classroom 24/7</strong> so you can
                    study at night, on the weekends, or during lunch breaks
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2802" data-mp="2hfgka5smrt42">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2803" data-mp="2hcu45hroc5a8" />
                  <span className="text-gray-700" data-id="element-2804" data-mp="2mbxar6nayjkb">
                    <strong data-id="element-2805" data-mp="3a3cg78adlgzs">Learn from expert faculty</strong> with real-world
                    experience in their respective fields
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2806" data-mp="305okaf11a3g1">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-2807" data-mp="304wmqynvlvn4" />
                  <span className="text-gray-700" data-id="element-2808" data-mp="352y85q8ry71r">
                    <strong data-id="element-2809" data-mp="1oqj7ujb3oqj8">Join the Honors Program</strong> for high-achieving
                    bachelor's students
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-2810" data-mp="6djvc8gu4z0">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop&q=80" alt="Student studying online" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-2811" data-mp="5u1xvitfgkz" />
            </div>
          </div>
        </div>
      </section>

      {/* Student Testimonials */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2812" data-mp="3cp3dx3dhcmd5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2813" data-mp="3clpd3zkgsl7t">
          <div className="flex flex-col lg:flex-row gap-0 rounded-2xl overflow-hidden" data-id="element-2814" data-mp="38kvo12vad0dc">
            {/* Left intro */}
            <div className="bg-[#1C2B39] p-8 lg:p-12 flex flex-col justify-center lg:w-1/4" data-id="element-2815" data-mp="33pgc4ggu9ejk">
              <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" data-id="element-2816" data-mp="2yq3q1xxskbi1">
                Student Experience
              </p>
              <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-2817" data-mp="wwe9fdgnsphd">
                Praise for UAGC
              </h2>
              <p className="text-white/70 text-sm mt-3" data-id="element-2818" data-mp="14fni14hcck5d">
                In their own words, why students love UAGC
              </p>
            </div>
            {/* Testimonial cards */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-0" data-id="element-2819" data-mp="1e79pcpqk5z2u">
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2820" data-mp="1j51s6esqr5lt">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80" alt="Vicente Rocco Vargas" className="w-full h-full object-cover" data-id="element-2821" data-mp="1j75uisazrwjz" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2822" data-mp="vepa4b3s7shi"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2823" data-mp="10gep21u4kqav">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2824" data-mp="26vp2t7u1k9c2">
                    "I've tried to send so many people to UAGC because of how
                    well they've treated me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2825" data-mp="2wykm3gsqf9mq">
                    Vicente "Rocco" Vargas
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2826" data-mp="36o44ymgg33hl">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop&q=80" alt="Jessica Smith" className="w-full h-full object-cover" data-id="element-2827" data-mp="31x479ezi4he1" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2828" data-mp="3bm5plbtirav6"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2829" data-mp="3plxa7vejjpi5">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2830" data-mp="3kondduotobka">
                    "Life has totally changed because there were so many things
                    that came out of this experience for me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2831" data-mp="1qbxhj7ew8sa9">
                    Jessica Smith
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-2832" data-mp="apbrp0etrmwf">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop&q=80" alt="Wendy Bosquez" className="w-full h-full object-cover" data-id="element-2833" data-mp="3s7o57iq9ggrt" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-2834" data-mp="3j557te58uwcy"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2835" data-mp="3o36pbun0unmb">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-2836" data-mp="399trwsi3nj3m">
                    "I believe that my future, my destiny is now bigger than my
                    dreams ever since being a part of UAGC."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-2837" data-mp="1cg0fa79nzoii">
                    Wendy Bosquez
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-2838" data-mp="2hqhep1g7galr">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2839" data-mp="2mpkk4n03kiu9">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2840" data-mp="2mnhit6thv3a8"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-6" data-id="element-2841" data-mp="2rhlrbuierjdf">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2842" data-mp="31e6x4i294yib">
            UAGC maintains institutional accreditation and several programmatic
            accreditations, which means that it consistently meets quality
            standards that all students deserve.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-2843" data-mp="3acso91lshlup">
            So, whether you are <strong data-id="element-2844" data-mp="1b3c3d1i2vwn4">transferring credits†</strong> you have
            earned from another institution, or you are just{' '}
            <strong data-id="element-2845" data-mp="1g2ykhm111x17">starting your college journey</strong>, you can rest assured
            knowing you are receiving the most for your investment at UAGC.
          </p>
        </div>
      </section>

      {/* Try Your First Course Free */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-2846" data-mp="yr07fk37pord">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2847" data-mp="tsgn1o49ol14">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-2848" data-mp="tu1j5i68cc13">
            Try Your First Course Complementary for 3 Weeks: No Cost, No Risk,
            Total Confidence
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10" data-id="element-2849" data-mp="1mhnnwtr71mpj">
            The UAGC Promise affords new degree-seeking students the opportunity
            to get a feel for the UAGC online platform before enrolling to make
            sure it's the right fit. With the UAGC Promise, attend for the first
            three weeks of your first course without any financial risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-2850" data-mp="1zpnp4raeg7n">
            <div className="flex flex-col items-center" data-id="element-2851" data-mp="3jhkfc7nc8m9">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2852" data-mp="8hj2bttc61w5">
                <ShieldCheckIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2853" data-mp="3ppwlrlazs43u" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2854" data-mp="3uor28glhlgks">
                Attend for 3 Weeks Risk Complementary
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2855" data-mp="3gwkpl4hjlrq3">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2856" data-mp="3lnsz00ivhd39">
                <DollarSignIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2857" data-mp="36zokl8qurnqa" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2858" data-mp="3bxzvlmsvw4a3">
                Start School with No Financial Burden
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-2859" data-mp="2xz9yw89z86m9">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-2860" data-mp="320bp5zlx18c3">
                <LaptopIcon className="w-8 h-8 text-[#CC0033]" data-id="element-2861" data-mp="2jct2j6m1pkdv" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-2862" data-mp="2ed8899htm07h">
                Flexibility to Find Your Fit
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C2B39] py-16" data-id="element-2863" data-mp="2ncbmo061lrfn">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2864" data-mp="2nfprdf26icq9">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-2865" data-mp="328t1509j3m6f"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-2866" data-mp="2xahqwofbn9no">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A843]" data-id="element-2867" data-mp="3b25z4gmg9dzd">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-2868" data-mp="372nfrpaa1ayn">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#1C2B39] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-2869" data-mp="373gp8y0meekh">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-2870" data-mp="7zax45znxh0m">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-2871" data-mp="cqt172wzfxpt">
          <p data-id="element-2872" data-mp="hpw3iflkvpu3">Certain degree programs may not be available in all states.</p>
          <p data-id="element-2873" data-mp="hs070wfkemtu">
            * Classes last 5 weeks for undergraduate programs, 6 weeks for
            graduate programs, and 9 weeks for doctoral capstone, planning and
            project classes.
          </p>
          <p data-id="element-2874" data-mp="3q5z27vupq46a">
            † The transferability of credits is subject to the University of
            Arizona Global Campus transfer credit policies and requires the
            submission of official transcripts. The official transcripts will be
            evaluated to determine the credits that will officially apply toward
            a UAGC degree program.
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-2875" data-mp="1agxowyfv6hm2">
            The University of Arizona Global Campus is accredited by the Higher
            Learning Commission (hlcommission.org), an institutional
            accreditation agency recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-2876" data-mp="2l4prwmerajn2">
            © {new Date().getFullYear()} University of Arizona Global Campus.
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}